import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { profileAPI, uploadAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Camera, Loader2, Save, Crown, Zap, Star, Globe, Info, Lock } from 'lucide-react';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [handle, setHandle] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    if (user) {
      setUsername(user.username || user.name || '');
      setHandle(user.handle || '');
      setPhone(user.phone || '');
      setAvatarUrl(user.avatar_url || '');
      setCustomDomain(user.custom_domain || '');
    }
  }, [user]);

  useEffect(() => {
    api.get('/plan').then(res => setPlanData(res.data)).catch(() => {});
  }, []);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.thumbnail(fd);
      setAvatarUrl(res.data.thumbnail_url);
      toast.success('Photo updated');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingAvatar(false); }
  };

  const formatHandle = (val) => {
    return val.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/--+/g, '-');
  };

  const handleSave = async () => {
    if (!username.trim()) { toast.error('Username is required'); return; }
    if (!handle.trim()) { toast.error('Store name is required'); return; }
    setSaving(true);
    try {
      const res = await profileAPI.update({
        username: username.trim(),
        handle: handle.trim(),
        avatar_url: avatarUrl,
        phone: phone.trim()
      });
      updateUser(res.data);
      toast.success('Profile saved!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const displayName = username || user?.name || '';

  return (
    <div className="space-y-6 max-w-[800px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>Profile</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your account settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="profile-save-button" style={{ backgroundColor: '#195259' }} className="text-white hover:opacity-90">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </div>

      {/* Avatar & Username */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="relative group">
              <Avatar className="h-20 w-20 border-2 border-border">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">{displayName?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
              </Avatar>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploadingAvatar} data-testid="profile-avatar-upload" />
                {uploadingAvatar ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Camera className="h-5 w-5 text-white" />}
              </label>
            </div>
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <Label>Username</Label>
                <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Your display name" data-testid="profile-username-input" />
                <p className="text-xs text-muted-foreground">This name is displayed in your dashboard, welcome message, and notifications.</p>
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+60 12-345 6789" data-testid="profile-phone-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Email</Label>
                <p className="text-sm font-medium">{user?.email}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Store Name (URL Handle) */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" style={{ color: '#195259' }} />
            <CardTitle className="text-base" style={{ color: '#195259' }}>Store Name</CardTitle>
          </div>
          <CardDescription>This is your unique store identifier used in your public URL</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Store Name</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 bg-muted text-muted-foreground text-xs whitespace-nowrap">{window.location.origin}/</span>
              <Input
                value={handle}
                onChange={e => setHandle(formatHandle(e.target.value))}
                placeholder="yourstore"
                className="rounded-l-none text-sm font-mono"
                data-testid="profile-handle-input"
              />
              <span className="inline-flex items-center px-3 rounded-r-lg border border-l-0 bg-muted text-muted-foreground text-xs whitespace-nowrap">/page-name</span>
            </div>
            <div className="flex items-start gap-2 mt-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(25,82,89,0.06)' }}>
              <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#195259' }} />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <p>This name will appear in your landing page URLs. Only lowercase letters, numbers, and dashes are allowed.</p>
                <p className="mt-1 font-medium text-foreground">Your store URL: <span className="font-mono text-primary">{window.location.origin}/{handle || 'yourstore'}/page-name</span></p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Subscription</CardTitle>
          <CardDescription>Your current plan and billing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl border" style={{ backgroundColor: 'rgba(25,82,89,0.06)', borderColor: '#195259' }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}>
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold capitalize" style={{ color: '#195259' }}>{planData?.plan || user?.plan || 'Free'} Plan</p>
                  <Badge className="text-[10px] text-white" style={{ backgroundColor: '#195259' }}>Current</Badge>
                </div>
                {planData?.plan_expires_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">Expires: {new Date(planData.plan_expires_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                )}
                {!planData?.plan_expires_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">{(planData?.plan || 'free') === 'free' ? 'Free forever' : 'Active subscription'}</p>
                )}
              </div>
            </div>
            <Button size="sm" className="font-semibold text-white" style={{ backgroundColor: '#195259' }} onClick={() => navigate('/app/plan')}>
              Manage Plan
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border p-4 transition-colors" style={{ borderColor: '#195259', borderRadius: '20px' }}>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4" style={{ color: '#FF9A00' }} />
                <p className="font-semibold text-sm" style={{ color: '#195259' }}>Pro Plan</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">15 products, 8 pages, remove branding</p>
              <p className="text-lg font-bold" style={{ color: '#195259' }}>RM 69<span className="text-xs font-normal text-muted-foreground">/month</span></p>
              <Button size="sm" className="w-full mt-3 font-semibold" style={{ backgroundColor: 'rgba(25,82,89,0.08)', color: '#195259' }} onClick={() => navigate('/app/plan')}>
                {(planData?.plan || 'free') === 'pro' ? 'Current' : 'View Plan'}
              </Button>
            </div>
            <div className="rounded-xl border p-4 transition-colors" style={{ borderColor: '#195259', borderRadius: '20px' }}>
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-4 w-4" style={{ color: '#FF9A00' }} />
                <p className="font-semibold text-sm" style={{ color: '#195259' }}>Premium Plan</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Unlimited everything, custom domain, AI Builder</p>
              <p className="text-lg font-bold" style={{ color: '#195259' }}>RM 99<span className="text-xs font-normal text-muted-foreground">/month</span></p>
              <Button size="sm" className="w-full mt-3 font-semibold text-white" style={{ backgroundColor: '#195259' }} onClick={() => navigate('/app/plan')}>
                {(planData?.plan || 'free') === 'premium' ? 'Current' : 'View Plan'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Domain */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" style={{ color: '#195259' }} />
            <CardTitle className="text-base" style={{ color: '#195259' }}>Custom Domain</CardTitle>
            {(planData?.plan || user?.plan || 'free') !== 'premium' && (
              <Badge className="text-[10px] text-white" style={{ backgroundColor: '#FF9A00' }}>Premium Only</Badge>
            )}
          </div>
          <CardDescription>Connect your own domain to your Funnely store</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(25,82,89,0.06)' }}>
            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#195259' }}>
              <Globe className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: '#195259' }}>Coming Soon</p>
              <p className="text-xs text-muted-foreground mt-0.5">Custom domain feature is under development. Stay tuned!</p>
            </div>
            <Badge className="text-[10px] text-white shrink-0" style={{ backgroundColor: '#FF9A00' }}>Premium</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Member since</p>
              <p className="text-xs text-muted-foreground">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
            </div>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Account ID</p>
              <p className="text-xs font-mono text-muted-foreground">{user?.id?.slice(0, 12)}...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
